/**
 * Day 83 P6.2 — Provenance derivation helper.
 *
 * Pure function consuming a per-field provenance envelope (Day 82 L1 shape:
 * `{source, set_at, set_by, previous_value, reason}`) and returning indicator
 * flags + extracted fields for downstream UI rendering.
 *
 * Stale computation is client-side per Sol-lock L3 (no `is_stale` RPC call —
 * reserved for server-side consumers). Italic taxonomy maps `directory_edit`
 * + `manual` to override semantics per L4 (override-class, not literal source
 * name match).
 *
 * Consumed by:
 *   - ProvenanceTooltip.tsx (italic + stale + tooltip body assembly)
 *   - Surface-level callers that need ind values for additional styling
 *     decisions (e.g. text-color mute on stale per L8 inline mode)
 */

import type { ProvenanceSource } from '../../lib/provenanceSources';

// L4 Sol-locked source-prefix taxonomy. Co-located with deriveProvenanceIndicator
// (this lib-style file is the canonical home; ProvenanceTooltip + Products.tsx
// column-toggle aggregation both consume from here). Lives here rather than in
// ProvenanceTooltip.tsx to avoid react-refresh/only-export-components warnings
// when consumers import non-component exports from a JSX-bearing module.
//
// Day 84.8 P2 — engine-flag values appended for cross-domain consumer fold-in
// per src/lib/engineFlags.ts unions. Distinct vocabulary from write-side
// provenance source labels per DOMAIN_ARCH §8.10 IA-7 (cross-engine
// non-unification). manual_override is the engine-side equivalent of the
// write-side `manual` label — same human meaning, different vocabulary.
const SOURCE_PREFIX: Record<string, string> = {
  algopix_import: 'From Algopix import',
  algopix_discovery: 'Discovered during Algopix import',
  directory_create: 'Created in Directory',
  directory_edit: 'Manually edited in Directory',
  manual: 'Manually overridden',
  // W4a — the in-house resolver asin source (write/read label symmetry; NOT an override → no italic).
  in_house_resolver: 'From in-house resolver',
  // Engine-flag values (Day 84.8 P2):
  manual_override: 'Manually overridden',
  per_path: 'From per-path config',
  algopix: 'From Algopix',
  isg_own: 'From ISG order history',
  // W4c — amazon_fee_source engine flag (product_profitability.amazon_fee_source; the COALESCE
  // tier that won: spapi → keepa → flat). Read-side render via sourcePrefix() direct-label.
  spapi: 'From SP-API',
  keepa: 'From Keepa estimate',
  flat: 'From flat fee',
};

// Day 84.8b P1 Lock 2 (#DBN-ζ disk-truth correction) — `snapshot` arg
// surfaces "Current source:" prefix at snapshot-surface wrap sites (PODetail
// Profit/Unit cell) where the rendered value is a snapshot from PO submit
// time but the source flag refers to current product_profitability cache
// state. Default false preserves existing 5 test-cohort assertions + all
// current-state-surface consumer call sites unchanged.
export function sourcePrefix(source: string | null, snapshot = false): string {
  const knownLabel = source ? SOURCE_PREFIX[source] : undefined;
  if (snapshot) {
    return `Current source: ${knownLabel ?? 'unknown'}`;
  }
  return knownLabel ?? 'Source: unknown';
}

interface ProvenanceEnvelope {
  source?: ProvenanceSource | string | null;
  reason?: string | null;
  set_at?: string | null;
  set_by?: string | null;
  previous_value?: unknown;
}

export interface ProvenanceMap {
  [fieldKey: string]: ProvenanceEnvelope;
}

export interface ProvenanceIndicator {
  stale: boolean;
  italic: boolean;
  source: ProvenanceSource | string | null;
  reason: string | null;
  setAt: string | null;
  setBy: string | null;
  hasOverrideChain: boolean;
  previousValue: unknown;
}

// L4 italic-source taxonomy — override semantics, not literal source-name match.
// directory_create is the baseline-create for non-Algopix entities (no override
// to italicize); algopix_import + algopix_discovery are API-sourced defaults.
// Day 84.8 P2: manual_override added (engine-flag equivalent of write-side
// `manual` per IA-7 cross-engine vocabulary non-unification).
const OVERRIDE_SOURCES = new Set<string>(['directory_edit', 'manual', 'manual_override']);

const MS_PER_DAY = 86_400_000;

const EMPTY_INDICATOR: ProvenanceIndicator = {
  stale: false,
  italic: false,
  source: null,
  reason: null,
  setAt: null,
  setBy: null,
  hasOverrideChain: false,
  previousValue: null,
};

/**
 * Derive UI-render indicator from a per-field provenance envelope.
 *
 * Returns a fresh object (no caching) — small enough to recompute per render;
 * consumers wrap with useMemo when invoked in render-heavy contexts (AG Grid
 * cellRenderers).
 *
 * @param provenance         The full provenance JSONB sidecar from a row (or
 *                           null/undefined when the row has no provenance).
 * @param fieldKey           Tracked-field key within the provenance map
 *                           (e.g. 'cost_usd', 'is_active', 'auto_seeded').
 * @param staleThresholdDays Days-old threshold; staleness = setAt older than
 *                           threshold. Resolved by consumer once per page from
 *                           `manual_override_stale_days` business param.
 */
export function deriveProvenanceIndicator(
  provenance: ProvenanceMap | null | undefined,
  fieldKey: string,
  staleThresholdDays: number
): ProvenanceIndicator {
  const envelope = provenance?.[fieldKey];
  if (!envelope) return EMPTY_INDICATOR;

  const source = envelope.source ?? null;
  const setAt = envelope.set_at ?? null;
  const reason = envelope.reason ?? null;
  const setBy = envelope.set_by ?? null;

  const italic = typeof source === 'string' && OVERRIDE_SOURCES.has(source);

  let stale = false;
  if (setAt) {
    const setAtMs = new Date(setAt).getTime();
    if (Number.isFinite(setAtMs)) {
      stale = Date.now() - setAtMs > staleThresholdDays * MS_PER_DAY;
    }
  }

  // L2 hasOverrideChain — `previous_value` key present, even when its scalar
  // value is null (covers "field went from null → set" override case).
  const hasOverrideChain = Object.prototype.hasOwnProperty.call(envelope, 'previous_value');

  return {
    stale,
    italic,
    source,
    reason,
    setAt,
    setBy,
    hasOverrideChain,
    previousValue: envelope.previous_value ?? null,
  };
}
